/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import ReactDatetime from 'react-datetime';
import moment, { Moment } from 'moment';
import NumberFormat from 'react-number-format';
import StateService from '../../../../services/State';
import { toast } from 'react-toastify';
import InputMask from 'react-input-mask';
import SignatureCanvas from 'react-signature-canvas';
import HireDocuments from '../../../../services/HireDocuments';

import { selectAllOnFocus } from '../../../../helpers/selectOnFocus';
export interface FormIT2104Data {
  year: string;
  firstNameMI: string;
  lastName: string;
  socialSecurityNumber: string;
  homeAddress: string;
  apartmentNumber: string;
  cityVillagePO: string;
  state: string;
  zipCode: string;
  filingStatus: 'single' | 'married' | 'married_single_rate' | '';
  isNyCityResident: 'yes' | 'no' | '';
  isYonkersResident: 'yes' | 'no' | '';
  allowancesLine1: string;
  allowancesLine2: string;
  nysAmountLine3: string;
  nycAmountLine4: string;
  yonkersAmountLine5: string;
  employeeSignature: string;
  signatureDate: string;
  claimedMoreThan14Exemptions: boolean;
  isNewHireOrRehire: boolean;
  firstDateServicesForPay: string;
  dependentHealthBenefitsAvailable: 'yes' | 'no' | '';
  dateEmployeeQualifiesForBenefits: string;
  employerNameAddress: string;
  employerIdNumber: string;
}

interface StateOption {
  id: string;
  uf: string;
  name: string;
}

interface FormIT2104ModalProps {
  isOpen: boolean;
  currentUser: any;
  token: string;
  toggle: () => void;
  onSubmit: (data: FormIT2104Data) => void;
  initialData?: Partial<FormIT2104Data>;
}

const FormIT2104Modal: React.FC<FormIT2104ModalProps> = ({
  isOpen,
  currentUser,
  token,
  toggle,
  onSubmit,
  initialData = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormIT2104Data>({
    year: moment().format('YYYY'),
    firstNameMI: '',
    lastName: '',
    socialSecurityNumber: '',
    homeAddress: '',
    apartmentNumber: '',
    cityVillagePO: '',
    state: '',
    zipCode: '',
    filingStatus: '',
    isNyCityResident: '',
    isYonkersResident: '',
    allowancesLine1: '0',
    allowancesLine2: '0',
    nysAmountLine3: '',
    nycAmountLine4: '',
    yonkersAmountLine5: '',
    employeeSignature: '',
    signatureDate: '',
    claimedMoreThan14Exemptions: false,
    isNewHireOrRehire: false,
    firstDateServicesForPay: '',
    dependentHealthBenefitsAvailable: '',
    dateEmployeeQualifiesForBenefits: '',
    employerNameAddress: '',
    employerIdNumber: '',
    ...initialData,
  });
  const [sigPad, setSigPad] = useState<any>();

  const [states, setStates] = useState<StateOption[]>([]);
  const [loadingStates, setLoadingStates] = useState<boolean>(false);

  useEffect(() => {
    const listStates = async () => {
      setLoadingStates(true);
      try {
        const result = await StateService('').listSelect();
        setStates(result.data || []);
        if (currentUser?.user?.addresses?.[0]?.stateId) {
          const state = result.data.find((s: { id: string }) => s.id === currentUser.user.addresses[0].stateId);
          if (state) {
            setFormData((prev) => ({ ...prev, state: state.uf }));
          }
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Error fetching states');
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };

    if (isOpen) {
      listStates();
      setFormData({
        year: moment().format('YYYY'),
        firstNameMI: currentUser?.user?.firstName || '',
        lastName: currentUser?.user?.lastName || '',
        socialSecurityNumber: '',
        homeAddress: currentUser?.user?.addresses?.[0]?.address || '',
        apartmentNumber: '',
        cityVillagePO: currentUser?.user?.addresses?.[0]?.city || '',
        state: currentUser?.user?.addresses?.[0]?.stateId || '',
        zipCode: currentUser?.user?.addresses?.[0]?.zipCode || '',

        filingStatus: '',
        isNyCityResident: '',
        isYonkersResident: '',
        allowancesLine1: '0',
        allowancesLine2: '0',
        nysAmountLine3: '',
        nycAmountLine4: '',
        yonkersAmountLine5: '',
        employeeSignature: '',
        signatureDate: '',
        claimedMoreThan14Exemptions: false,
        isNewHireOrRehire: false,
        firstDateServicesForPay: '',
        dependentHealthBenefitsAvailable: '',
        dateEmployeeQualifiesForBenefits: '',
        employerNameAddress: '',
        employerIdNumber: '',
        ...initialData,
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    const img = sigPad?.getTrimmedCanvas()?.toDataURL('image/png');
    const body = {
      type: 'IT2104',
      userId: currentUser?.userId || '',
      userName: currentUser?.userName || '',
      userToken: token,
      pdf: '',
      pdfUrl: 'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/it-2104-form.pdf',
      pdfSigned: img,
      signature: img,
      options: { ...formData, signatureDate: moment().format('YYYY-MM-DD') },
    };

    try {
      await HireDocuments('').create(body);
      toast.success(`Saved successfully!`);
      onSubmit(formData);
    } catch (e: any) {
      console.log(e);
      const message = String(e?.response?.data?.message || 'Create error!');
      toast.error(message);
    }

    setLoading(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;
    const isCheckbox = type === 'checkbox';
    const inputValue = isCheckbox ? (event.target as HTMLInputElement).checked : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: inputValue,
    }));
  };

  const handleRadioChange = (name: keyof FormIT2104Data, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleNumericInputChange = (name: keyof FormIT2104Data) => (values: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: values.value,
    }));
  };

  const handleDateChange = (name: keyof FormIT2104Data) => (date: string | Moment) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: moment.isMoment(date) ? date.format('MM/DD/YYYY') : String(date),
    }));
  };

  const handleCancel = () => {
    toggle();
  };

  return (
    <Modal className="modal-dialog-centered" size="xl" isOpen={isOpen} toggle={handleCancel} backdrop="static">
      <ModalHeader toggle={handleCancel}>IT-2104: Employee's Withholding Allowance Certificate</ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md="5">
              <FormGroup>
                <Label htmlFor="it2104ModalFirstNameMI">First name and middle initial</Label>
                <Input
                  type="text"
                  name="firstNameMI"
                  id="it2104ModalFirstNameMI"
                  value={formData.firstNameMI}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label htmlFor="it2104ModalLastName">Last name</Label>
                <Input
                  type="text"
                  name="lastName"
                  id="it2104ModalLastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label htmlFor="it2104ModalSocialSecurityNumber">Your Social Security number</Label>
                <Input
                  type="text"
                  name="socialSecurityNumber"
                  tag={InputMask}
                  mask="999-99-9999"
                  id="it2104ModalSocialSecurityNumber"
                  value={formData.socialSecurityNumber}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="8">
              <FormGroup>
                <Label htmlFor="it2104ModalHomeAddress">
                  Permanent home address (number and street or rural route)
                </Label>
                <Input
                  type="text"
                  name="homeAddress"
                  id="it2104ModalHomeAddress"
                  value={formData.homeAddress}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label htmlFor="it2104ModalApartmentNumber">Apartment number</Label>
                <Input
                  type="text"
                  name="apartmentNumber"
                  id="it2104ModalApartmentNumber"
                  value={formData.apartmentNumber}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label htmlFor="it2104ModalCityVillagePO">City, village, or post office</Label>
                <Input
                  type="text"
                  name="cityVillagePO"
                  id="it2104ModalCityVillagePO"
                  value={formData.cityVillagePO}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label htmlFor="it2104ModalStateField">State</Label>
                <Input
                  type="select"
                  name="state"
                  id="it2104ModalStateField"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  disabled={loadingStates}
                >
                  <option value="">Select State...</option>
                  {loadingStates ? (
                    <option disabled>Loading states...</option>
                  ) : (
                    states.map((stateOpt) => (
                      <option key={stateOpt.uf} value={stateOpt.uf}>
                        {stateOpt.uf}
                      </option>
                    ))
                  )}
                </Input>
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label htmlFor="it2104ModalZipCode">ZIP code</Label>
                <Input
                  type="text"
                  name="zipCode"
                  id="it2104ModalZipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>
              Filing Status (Mark an X in the single or Head of household box if you are married but legally separated)
            </Label>
            <div>
              <FormGroup check inline>
                <Input
                  type="radio"
                  name="filingStatus"
                  id="it2104ModalStatusSingle"
                  value="single"
                  checked={formData.filingStatus === 'single'}
                  onChange={(e) => handleRadioChange('filingStatus', e.target.value)}
                  required
                />
                <Label check htmlFor="it2104ModalStatusSingle">
                  Single or Head of household
                </Label>
              </FormGroup>
              <FormGroup check inline>
                <Input
                  type="radio"
                  name="filingStatus"
                  id="it2104ModalStatusMarried"
                  value="married"
                  checked={formData.filingStatus === 'married'}
                  onChange={(e) => handleRadioChange('filingStatus', e.target.value)}
                />
                <Label check htmlFor="it2104ModalStatusMarried">
                  Married
                </Label>
              </FormGroup>
              <FormGroup check inline>
                <Input
                  type="radio"
                  name="filingStatus"
                  id="it2104ModalStatusMarriedSingleRate"
                  value="married_single_rate"
                  checked={formData.filingStatus === 'married_single_rate'}
                  onChange={(e) => handleRadioChange('filingStatus', e.target.value)}
                />
                <Label check htmlFor="it2104ModalStatusMarriedSingleRate">
                  Married, but withhold at higher single rate
                </Label>
              </FormGroup>
            </div>
          </FormGroup>

          <Row className="mt-3">
            <Col md="8">
              <Label>
                Are you a resident of New York City (this includes the Bronx, Brooklyn, Manhattan, Queens, and Staten
                Island)?
              </Label>
            </Col>
            <Col md="4">
              <FormGroup check inline>
                <Input
                  type="radio"
                  name="isNyCityResident"
                  id="it2104ModalNyCityYes"
                  value="yes"
                  checked={formData.isNyCityResident === 'yes'}
                  onChange={(e) => handleRadioChange('isNyCityResident', e.target.value)}
                  required
                />
                <Label check htmlFor="it2104ModalNyCityYes">
                  Yes
                </Label>
              </FormGroup>
              <FormGroup check inline>
                <Input
                  type="radio"
                  name="isNyCityResident"
                  id="it2104ModalNyCityNo"
                  value="no"
                  checked={formData.isNyCityResident === 'no'}
                  onChange={(e) => handleRadioChange('isNyCityResident', e.target.value)}
                />
                <Label check htmlFor="it2104ModalNyCityNo">
                  No
                </Label>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="8">
              <Label>Are you a resident of Yonkers?</Label>
            </Col>
            <Col md="4">
              <FormGroup check inline>
                <Input
                  type="radio"
                  name="isYonkersResident"
                  id="it2104ModalYonkersYes"
                  value="yes"
                  checked={formData.isYonkersResident === 'yes'}
                  onChange={(e) => handleRadioChange('isYonkersResident', e.target.value)}
                  required
                />
                <Label check htmlFor="it2104ModalYonkersYes">
                  Yes
                </Label>
              </FormGroup>
              <FormGroup check inline>
                <Input
                  type="radio"
                  name="isYonkersResident"
                  id="it2104ModalYonkersNo"
                  value="no"
                  checked={formData.isYonkersResident === 'no'}
                  onChange={(e) => handleRadioChange('isYonkersResident', e.target.value)}
                />
                <Label check htmlFor="it2104ModalYonkersNo">
                  No
                </Label>
              </FormGroup>
            </Col>
          </Row>
          <p className="mt-2" style={{ fontSize: '0.8rem' }}>
            <strong>
              Before making any entries, see the Note below, and if applicable, complete the worksheet in the
              instructions.
            </strong>
          </p>

          <Row className="align-items-center mt-2">
            <Col md="10">
              <Label htmlFor="it2104ModalAllowancesLine1">
                1. Total number of allowances you are claiming for New York State and Yonkers, if applicable (from line
                19, if using worksheet)
              </Label>
            </Col>
            <Col md="2">
              <NumberFormat
                {...selectAllOnFocus}
                name="allowancesLine1"
                id="it2104ModalAllowancesLine1"
                className="form-control text-right"
                value={formData.allowancesLine1}
                onValueChange={handleNumericInputChange('allowancesLine1')}
                thousandSeparator={false}
                allowNegative={false}
                decimalScale={0}
                required
              />
            </Col>
          </Row>
          <Row className="align-items-center mt-2">
            <Col md="10">
              <Label htmlFor="it2104ModalAllowancesLine2">
                2. Total number of allowances for New York City (from line 31, if using worksheet)
              </Label>
            </Col>
            <Col md="2">
              <NumberFormat
                {...selectAllOnFocus}
                name="allowancesLine2"
                id="it2104ModalAllowancesLine2"
                className="form-control text-right"
                value={formData.allowancesLine2}
                onValueChange={handleNumericInputChange('allowancesLine2')}
                thousandSeparator={false}
                allowNegative={false}
                decimalScale={0}
                required
              />
            </Col>
          </Row>

          <p className="mt-2" style={{ fontSize: '0.8rem' }}>
            <strong>
              Use lines 3, 4, and 5 below to have additional withholding per pay period under special agreement with
              your employer.
            </strong>
          </p>
          <Row className="align-items-center mt-2">
            <Col md="10">
              <Label htmlFor="it2104ModalNysAmountLine3">3. New York State amount</Label>
            </Col>
            <Col md="2">
              <NumberFormat
                {...selectAllOnFocus}
                name="nysAmountLine3"
                id="it2104ModalNysAmountLine3"
                className="form-control text-right"
                value={formData.nysAmountLine3}
                onValueChange={handleNumericInputChange('nysAmountLine3')}
                thousandSeparator={true}
                prefix={'$'}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
              />
            </Col>
          </Row>
          <Row className="align-items-center mt-2">
            <Col md="10">
              <Label htmlFor="it2104ModalNycAmountLine4">4. New York City amount</Label>
            </Col>
            <Col md="2">
              <NumberFormat
                {...selectAllOnFocus}
                name="nycAmountLine4"
                id="it2104ModalNycAmountLine4"
                className="form-control text-right"
                value={formData.nycAmountLine4}
                onValueChange={handleNumericInputChange('nycAmountLine4')}
                thousandSeparator={true}
                prefix={'$'}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
              />
            </Col>
          </Row>
          <Row className="align-items-center mt-2 mb-3">
            <Col md="10">
              <Label htmlFor="it2104ModalYonkersAmountLine5">5. Yonkers amount</Label>
            </Col>
            <Col md="2">
              <NumberFormat
                {...selectAllOnFocus}
                name="yonkersAmountLine5"
                id="it2104ModalYonkersAmountLine5"
                className="form-control text-right"
                value={formData.yonkersAmountLine5}
                onValueChange={handleNumericInputChange('yonkersAmountLine5')}
                thousandSeparator={true}
                prefix={'$'}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
              />
            </Col>
          </Row>

          <p style={{ fontSize: '0.9rem' }}>
            I certify that I am entitled to the number of withholding allowances claimed on this certificate.
          </p>
          <p style={{ fontSize: '0.8rem' }}>
            <strong>Penalty</strong> – A penalty of $500 may be imposed for any false statement you make that decreases
            the amount of money you have withheld from your wages. You may also be subject to criminal penalties.
          </p>
          <Row className="mt-3">
            <Col md="8">
              <FormGroup>
                <Row>
                  <Col md="12">
                    <Label for="employeeSignature">Employee's signature</Label>
                  </Col>
                  <Col md="12">
                    <SignatureCanvas
                      penColor="blue"
                      canvasProps={{ width: 500, height: 200, className: 'sig-canvas' }}
                      ref={(ref) => setSigPad(ref)}
                    />
                  </Col>
                  <Col md="12">
                    <Button
                      onClick={() => {
                        sigPad?.clear();
                      }}
                      color="warning"
                      size="sm"
                      className="mt-1 mb-2"
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label htmlFor="it2104ModalSignatureDate">Date</Label>
                <InputGroup>
                  <ReactDatetime
                    value={formData.signatureDate ? moment(formData.signatureDate, 'MM/DD/YYYY') : ''}
                    onChange={handleDateChange('signatureDate')}
                    dateFormat="MM/DD/YYYY"
                    timeFormat={false}
                    inputProps={{
                      id: 'it2104ModalSignatureDate',
                      placeholder: 'MM/DD/YYYY',
                      required: true,
                      name: 'signatureDate',
                    }}
                    closeOnSelect
                  />
                  <InputGroupText>
                    <i className="ni ni-calendar-grid-58" />
                  </InputGroupText>
                </InputGroup>
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />
          <h5>Employer: Keep this certificate with your records.</h5>
          <p style={{ fontSize: '0.8rem' }}>
            If any of the following apply, mark an <strong>X</strong> in each corresponding box, complete the additional
            information requested, and send an additional copy of this form to New York State. See{' '}
            <strong>Employer</strong> in the instructions. Visit{' '}
            <a href="http://www.tax.ny.gov" target="_blank" rel="noopener noreferrer">
              www.tax.ny.gov
            </a>{' '}
            (search: IT-2104-I) or scan the QR code below.
          </p>

          <FormGroup check className="mt-3">
            <Input
              type="checkbox"
              name="claimedMoreThan14Exemptions"
              id="it2104ModalClaimedMoreThan14"
              checked={formData.claimedMoreThan14Exemptions}
              onChange={handleInputChange}
            />
            <Label check htmlFor="it2104ModalClaimedMoreThan14" className="ml-2">
              A. Employee claimed more than 14 exemption allowances for New York State
            </Label>
          </FormGroup>
          <Row className="align-items-center mt-2">
            <Col md="auto">
              <FormGroup check>
                <Input
                  type="checkbox"
                  name="isNewHireOrRehire"
                  id="it2104ModalIsNewHireOrRehire"
                  checked={formData.isNewHireOrRehire}
                  onChange={handleInputChange}
                />
                <Label check htmlFor="it2104ModalIsNewHireOrRehire" className="ml-2">
                  B. Employee is a new hire or a rehire ... B
                </Label>
              </FormGroup>
            </Col>
            <Col>
              <FormGroup row className="align-items-center mb-0">
                <Label sm={7} className="mb-0 pr-1 text-right" style={{ fontSize: '0.85rem' }}>
                  First date employee performed services for pay (mm-dd-yyyy) (see Box B instructions):
                </Label>
                <Col sm={5}>
                  <InputGroup size="sm">
                    <ReactDatetime
                      value={
                        formData.firstDateServicesForPay ? moment(formData.firstDateServicesForPay, 'MM/DD/YYYY') : ''
                      }
                      onChange={handleDateChange('firstDateServicesForPay')}
                      dateFormat="MM/DD/YYYY"
                      timeFormat={false}
                      inputProps={{
                        id: 'it2104ModalFirstDateServices',
                        placeholder: 'MM/DD/YYYY',
                        name: 'firstDateServicesForPay',
                      }}
                      closeOnSelect
                    />
                    <InputGroupText>
                      <i className="ni ni-calendar-grid-58" />
                    </InputGroupText>
                  </InputGroup>
                </Col>
              </FormGroup>
            </Col>
          </Row>

          <p className="mt-3" style={{ fontSize: '0.8rem' }}>
            You may report new hire information online instead of mailing the form to New York State. Visit{' '}
            <a href="http://www.nynewhire.com" target="_blank" rel="noopener noreferrer">
              www.nynewhire.com
            </a>
            .
          </p>
          <p style={{ fontSize: '0.8rem' }}>
            <strong>Note:</strong> Employers <strong>must</strong> report individuals under an{' '}
            <strong>independent contractor arrangement</strong> with contracts in excess of $2,500 using the online
            reporting website above, <strong>not</strong> Form IT-2104.
          </p>

          <Row className="align-items-center mt-3">
            <Col md="auto">
              <Label>Are dependent health insurance benefits available for this employee?</Label>
            </Col>
            <Col md="auto">
              <FormGroup check inline>
                <Input
                  type="radio"
                  name="dependentHealthBenefitsAvailable"
                  id="it2104ModalDependentHealthYes"
                  value="yes"
                  checked={formData.dependentHealthBenefitsAvailable === 'yes'}
                  onChange={(e) => handleRadioChange('dependentHealthBenefitsAvailable', e.target.value)}
                />
                <Label check htmlFor="it2104ModalDependentHealthYes">
                  Yes
                </Label>
              </FormGroup>
              <FormGroup check inline>
                <Input
                  type="radio"
                  name="dependentHealthBenefitsAvailable"
                  id="it2104ModalDependentHealthNo"
                  value="no"
                  checked={formData.dependentHealthBenefitsAvailable === 'no'}
                  onChange={(e) => handleRadioChange('dependentHealthBenefitsAvailable', e.target.value)}
                />
                <Label check htmlFor="it2104ModalDependentHealthNo">
                  No
                </Label>
              </FormGroup>
            </Col>
            {formData.dependentHealthBenefitsAvailable === 'yes' && (
              <Col>
                <FormGroup row className="align-items-center mb-0">
                  <Label sm={6} className="mb-0 pr-1 text-right" style={{ fontSize: '0.85rem' }}>
                    If Yes, enter the date the employee qualifies (mm-dd-yyyy):
                  </Label>
                  <Col sm={6}>
                    <InputGroup size="sm">
                      <ReactDatetime
                        value={
                          formData.dateEmployeeQualifiesForBenefits
                            ? moment(formData.dateEmployeeQualifiesForBenefits, 'MM/DD/YYYY')
                            : ''
                        }
                        onChange={handleDateChange('dateEmployeeQualifiesForBenefits')}
                        dateFormat="MM/DD/YYYY"
                        timeFormat={false}
                        inputProps={{
                          id: 'it2104ModalDateEmployeeQualifies',
                          placeholder: 'MM/DD/YYYY',
                          name: 'dateEmployeeQualifiesForBenefits',
                        }}
                        closeOnSelect
                      />
                      <InputGroupText>
                        <i className="ni ni-calendar-grid-58" />
                      </InputGroupText>
                    </InputGroup>
                  </Col>
                </FormGroup>
              </Col>
            )}
          </Row>

          <Row className="mt-4">
            <Col md="8">
              <FormGroup>
                <Label htmlFor="it2104ModalEmployerNameAddress">
                  Employer's name and address (Employer: complete this section only if you are sending a copy of this
                  form to the New York State Tax Department.)
                </Label>
                <Input
                  type="textarea"
                  name="employerNameAddress"
                  id="it2104ModalEmployerNameAddress"
                  rows="3"
                  value={formData.employerNameAddress}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label htmlFor="it2104ModalEmployerIdNumber">Employer identification number</Label>
                <Input
                  type="text"
                  name="employerIdNumber"
                  id="it2104ModalEmployerIdNumber"
                  value={formData.employerIdNumber}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          Submit
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FormIT2104Modal;
